import { UploadIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormErrorMessage } from '@/components/form/form-error-message';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  getSignedProformaSendDefaultMessage,
  getUnsignedProformaWaitingForSupplierAgreementDefaultMessage,
} from '@/features/purchase-order/config/purchase-order-send-default-message';
import { usePurchaseOrderDocumentForm } from '@/features/purchase-order/hooks/use-purchase-order-document-form';
import { PURCHASE_ORDER_DOCUMENT_ACCEPT } from '@/features/purchase-order/lib/purchase-order-document-allowed-files';
import { PurchaseOrderDocumentFormFields } from '@/features/purchase-order/purchase-order-document-form-fields';
import { PurchaseOrderSendRecipients } from '@/features/purchase-order/purchase-order-send-recipients';
import { PROFORMA_SIGNED_DOCUMENT_TYPE_CODE } from '@/services/apis/mutations/purchase-orders/upload-signed-proforma-document';
import { PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE } from '@/services/apis/mutations/purchase-orders/upload-unsigned-proforma-document';

type PurchaseOrderDocumentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string;
  productionDeclarationId?: string | null;
  productionDeclarationToken?: string | null;
  supplierName?: string | null;
  fixedDocumentTypeCode?: string;
  title?: string;
  description?: string;
  includeMailFields?: boolean;
  isConcernedByProductionDeclaration?: boolean;
  initialRecipientEmails?: string[];
  initialRecipientLabels?: Record<string, string>;
};

export function PurchaseOrderDocumentDialog({
  open,
  onOpenChange,
  purchaseOrderId,
  productionDeclarationId,
  productionDeclarationToken,
  supplierName,
  fixedDocumentTypeCode,
  title,
  description,
  includeMailFields = false,
  isConcernedByProductionDeclaration = false,
  initialRecipientEmails = [],
  initialRecipientLabels = {},
}: PurchaseOrderDocumentDialogProps) {
  const { t } = useTranslation('purchaseOrder');
  const { t: tCommon } = useTranslation('common');
  const supplierPortalLink =
    productionDeclarationId && productionDeclarationToken
      ? `${window.location.origin}/production-declarations/${productionDeclarationId}?token=${encodeURIComponent(productionDeclarationToken)}`
      : null;
  const defaultMessage =
    fixedDocumentTypeCode === PROFORMA_SIGNED_DOCUMENT_TYPE_CODE
      ? getSignedProformaSendDefaultMessage(isConcernedByProductionDeclaration)
      : fixedDocumentTypeCode === PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE &&
          includeMailFields
        ? getUnsignedProformaWaitingForSupplierAgreementDefaultMessage(
            supplierName,
            supplierPortalLink,
          )
        : '';
  const [recipients, setRecipients] = useState<string[]>([]);
  const [message, setMessage] = useState(defaultMessage);
  const wasOpenRef = useRef(false);
  const isUnsignedProformaProductionDeclarationFlow =
    fixedDocumentTypeCode === PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE &&
    includeMailFields &&
    isConcernedByProductionDeclaration;
  const {
    control,
    dropZoneClassName,
    fileInputRef,
    formState,
    handleCancel,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    handleSubmit,
    isSubmitting,
    onFileInputChange,
    onSubmit,
    openFilePicker,
    showDocumentTypeField,
    selectedFile,
  } = usePurchaseOrderDocumentForm(
    {
      fixedDocumentTypeCode,
      onOpenChange,
      open,
      purchaseOrderId,
    },
    includeMailFields ? { message, recipients } : undefined,
  );

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setRecipients(
        initialRecipientEmails.length > 0 ? [...initialRecipientEmails] : [],
      );
      setMessage(defaultMessage);
    }
    wasOpenRef.current = open;
  }, [defaultMessage, open, initialRecipientEmails]);

  const documentUploadField = (
    <>
      <input
        accept={PURCHASE_ORDER_DOCUMENT_ACCEPT}
        className='sr-only'
        onChange={onFileInputChange}
        ref={fileInputRef}
        type='file'
      />

      <div className='flex flex-col gap-2'>
        <button
          className={dropZoneClassName}
          onClick={openFilePicker}
          onDragEnter={handleDropZoneDragEnter}
          onDragLeave={handleDropZoneDragLeave}
          onDragOver={handleDropZoneDragOver}
          onDrop={handleDropZoneDrop}
          type='button'
        >
          <UploadIcon aria-hidden className='size-10 text-muted-foreground' />
          {selectedFile ? (
            <p className='text-center font-medium text-foreground text-sm'>
              {selectedFile.name}
            </p>
          ) : (
            <p className='text-center text-muted-foreground text-sm'>
              {t('documentsDropzoneHint')}
            </p>
          )}
        </button>
        <FormErrorMessage error={formState.errors.file?.message} />
      </div>
    </>
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{title ?? t('documentsModalTitle')}</DialogTitle>
          <DialogDescription>
            {description ?? t('documentsModalDescription')}
          </DialogDescription>
        </DialogHeader>

        <form
          className='mt-4 flex flex-col gap-4'
          onSubmit={handleSubmit(onSubmit)}
        >
          {showDocumentTypeField && (
            <>
              <Alert>
                <AlertDescription>
                  {t('documentsOtherOnlyWarning')}
                </AlertDescription>
              </Alert>
              <PurchaseOrderDocumentFormFields
                control={control}
                documentTypeError={formState.errors.documentTypeId?.message}
              />
            </>
          )}

          {isUnsignedProformaProductionDeclarationFlow && documentUploadField}

          {includeMailFields && (
            <>
              {isUnsignedProformaProductionDeclarationFlow && (
                <>
                  <Separator />
                  <p className='text-muted-foreground text-sm'>
                    {t('documentUploadBadges.unsignedProformaEudrEmailContext')}
                  </p>
                </>
              )}
              <PurchaseOrderSendRecipients
                isSending={isSubmitting}
                onChange={setRecipients}
                open={open}
                recipientLabels={initialRecipientLabels}
                recipients={recipients}
              />
              <div className='flex flex-col gap-2'>
                <Label htmlFor='upload-message'>
                  {t('sendPoModalMessage')}
                </Label>
                <Textarea
                  className='field-sizing-fixed max-h-60 min-h-24 overflow-y-auto'
                  id='upload-message'
                  onChange={(event) => setMessage(event.target.value)}
                  rows={8}
                  value={message}
                />
              </div>
            </>
          )}

          {!isUnsignedProformaProductionDeclarationFlow && documentUploadField}

          <DialogFooter>
            <Button onClick={handleCancel} type='button' variant='outline'>
              {tCommon('cancel')}
            </Button>
            <Button disabled={isSubmitting} type='submit'>
              {includeMailFields ? t('sendPoModalSend') : tCommon('add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
